import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import fragment from "./shader/fragment.glsl";
import vertex from "./shader/vertex.glsl";
import GUI from "lil-gui";
import gsap from "gsap";
import ob1 from "../ob1.glb";
import ob2 from "../ob2.glb";
import ob3 from "../ob3.glb";
import matcap from "../matcap.png";
import scan from "../scan.png";

import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { SSRPass } from "three/examples/jsm/postprocessing/SSRPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { GammaCorrectionShader } from "three/examples/jsm/shaders/GammaCorrectionShader.js";
import { ReflectorForSSRPass } from "three/examples/jsm/objects/ReflectorForSSRPass.js";

export default class Sketch {
  constructor(options) {
    this.scene = new THREE.Scene();

    this.dummy = new THREE.Object3D();

    this.mobile = !(typeof screen.orientation !== "undefined");

    this.container = options.dom;
    this.width = this.container.offsetWidth;
    this.height = this.container.offsetHeight;
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(this.width, this.height);
    this.renderer.setClearColor(0xeeeeee, 1);
    this.renderer.physicallyCorrectLights = true;
    this.renderer.outputEncoding = THREE.sRGBEncoding;

    this.container.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(
      25,
      this.width / this.height,
      1,
      100
    );

    var frustumSize = 4;
    var aspect = window.innerWidth / window.innerHeight;
    // this.camera = new THREE.OrthographicCamera( frustumSize * aspect / - 2, frustumSize * aspect / 2, frustumSize / 2, frustumSize / - 2, -1000, 1000 );
    this.camera.position.set(8, 12, 16);
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.time = 0;

    this.dracoLoader = new DRACOLoader();
    this.dracoLoader.setDecoderPath(
      "https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/js/libs/draco/"
    ); // use a full url path
    this.gltf = new GLTFLoader();
    this.gltf.setDRACOLoader(this.dracoLoader);

    this.isPlaying = true;

    this.addObjects();
    this.initPost();
    this.resize();
    this.render();
    this.setupResize();
    // this.settings();
  }

  initPost() {
    this.composer = new EffectComposer(this.renderer);
    this.ssrPass = new SSRPass({
      renderer: this.renderer,
      scene: this.scene,
      camera: this.camera,
      width: this.width,
      height: this.height,
      groundReflector: null,
      selects: null,
    });
    this.ssrPass.maxDistance = 0.3;

    this.composer.addPass(this.ssrPass);
  }

  settings() {
    let that = this;
    this.settings = {
      progress: 0,
    };
    this.gui = new GUI();
    this.gui.add(this.settings, "progress", 0, 1, 0.01);
  }

  setupResize() {
    window.addEventListener("resize", this.resize.bind(this));
  }

  resize() {
    this.width = this.container.offsetWidth;
    this.height = this.container.offsetHeight;
    this.renderer.setSize(this.width, this.height);
    this.composer.setSize(this.width, this.height);
    this.camera.aspect = this.width / this.height;

    this.camera.updateProjectionMatrix();
  }

  async addObjects() {
    let that = this;
    this.material = new THREE.ShaderMaterial({
      extensions: {
        derivatives: "#extension GL_OES_standard_derivatives : enable",
      },
      side: THREE.DoubleSide,
      uniforms: {
        time: { value: 0 },
        uMatcap: { value: new THREE.TextureLoader().load(matcap) },
        uScan: { value: new THREE.TextureLoader().load(scan) },
        resolution: { value: new THREE.Vector4() },
      },
      // wireframe: true,
      // transparent: true,
      vertexShader: vertex,
      fragmentShader: fragment,
    });

    this.geometry = new THREE.PlaneGeometry(1, 1, 1, 1);

    let { scene: children } = await this.gltf.loadAsync(ob1);
    let geo1 = children.children[0].geometry;

    let { scene: children1 } = await this.gltf.loadAsync(ob2);
    let geo2 = children1.children[0].geometry;

    let { scene: children2 } = await this.gltf.loadAsync(ob3);
    let geo3 = children2.children[0].geometry;

    let mat = new THREE.MeshMatcapMaterial({
      matcap: new THREE.TextureLoader().load(matcap),
    });

    let rows = 20;
    this.count = rows * rows;
    let random = new Float32Array(this.count);
    this.instanced = new THREE.InstancedMesh(geo1, this.material, this.count);

    this.scene.add(this.instanced);
    let index = 0;
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < rows; j++) {
        random[index] = Math.random();
        this.dummy.position.set(
          i - rows / 2,
          -10 + Math.random(),
          j - rows / 2
        );
        this.dummy.updateMatrix();
        this.instanced.setMatrixAt(index++, this.dummy.matrix);
      }
    }

    this.instanced.instanceMatrix.needsUpdate = true;
    // add random values to the geometry
    this.instanced.geometry.setAttribute(
      "aRandom",
      new THREE.InstancedBufferAttribute(random, 1)
    );

    // this.plane = new THREE.Mesh(this.geometry, this.material);
    // this.scene.add(this.plane);
  }

  stop() {
    this.isPlaying = false;
  }

  play() {
    if (!this.isPlaying) {
      this.isPlaying = true;
      this.render();
    }
  }

  render() {
    if (!this.isPlaying) return;
    this.time += 0.01;
    this.material.uniforms.time.value = this.time;
    this.camera.position.x = 12 * Math.sin(this.time / 10);
    this.camera.position.z = 12 * Math.cos(this.time / 10);
    this.camera.position.y = 7;
    this.camera.lookAt(0, 0, 0);
    requestAnimationFrame(this.render.bind(this));
    if (this.mobile) {
      this.renderer.render(this.scene, this.camera);
    } else {
      this.composer.render();
    }
    //
  }
}

new Sketch({
  dom: document.getElementById("container"),
});
